import { useMemo, useState } from "react";
import { useTranslations } from "next-intl";
import { IconLock } from "@tabler/icons-react";
import { Field } from "@/types/Field";
import { DataType } from "@/types/enums/DataType";
import { CreationFieldsRef } from "../PCreateGuest";
import { useRole } from "@/providers/RoleProvider";
import { evaluateConditionGroup } from "@/lib/utils/evaluate-condition";
import { PDeleteConfirmation } from "../../PDeleteConfirmation/PDeleteConfirmation";
import { hasSuppliedFieldValue } from "@/lib/utils/input-validator";

/**
 * Shared "lock-to-edit" machinery for conditional BUILT-IN guest fields in the
 * create/edit popup (GeneralInfos / ContactInfos). Mirrors the additional-field
 * behaviour in AdditionFields.tsx: a field whose visibility/editable condition
 * the guest's current answers don't satisfy is rendered LOCKED (inert input +
 * lock icon); editing it is a deliberate two-step confirm. The public form and
 * backend enforce the same conditions strictly.
 *
 * The eval context is the whole `creationFields` shape (built-in values at the
 * top level + the additional-field map with defaults seeded), identical to what
 * AdditionFields builds, so a built-in condition can reference both built-in and
 * additional fields and resolve them the same way the backend will.
 */
export function useConditionalFieldLock(
  creationFields: CreationFieldsRef,
  allFields: Field[],
) {
  const t = useTranslations("popup-create-guest");
  const { legacyRoleFloor } = useRole();
  // Unlock state keyed by the STABLE identity (fieldId for custom; name for
  // built-ins, which have no fieldId) so a rename can't drop or leak an unlock.
  const [unlocked, setUnlocked] = useState<Set<string>>(new Set());
  const [pending, setPending] = useState<{ field: Field; label: string } | null>(
    null,
  );

  const evalData = useMemo(() => {
    const additionalFields: Record<string, unknown> = {
      ...(creationFields.additionalFields ?? {}),
    };
    for (const f of allFields) {
      // Blank-aware — see `hasSuppliedFieldValue`. The condition engine must
      // judge the values the backend will actually store.
      if (
        f.additionalField &&
        !hasSuppliedFieldValue(additionalFields, f.name) &&
        f.defaultValue != null
      ) {
        additionalFields[f.name] = f.defaultValue;
      }
    }
    return { ...creationFields, additionalFields };
  }, [creationFields, allFields]);

  const keyOf = (field: Field) => field.fieldId ?? field.name;

  /**
   * Lock state for one field. `label` is the human label shown in the confirm
   * dialog / aria (built-ins pass their translated guest-fields label).
   */
  const getLock = (field: Field, label: string) => {
    const conditionUnmet =
      (field.visibilityCondition &&
        !evaluateConditionGroup(field.visibilityCondition, evalData)) ||
      (field.editableCondition &&
        !evaluateConditionGroup(field.editableCondition, evalData));
    const isLocked =
      !!conditionUnmet &&
      !unlocked.has(keyOf(field)) &&
      legacyRoleFloor !== "VIEWER";
    return {
      conditionUnmet: !!conditionUnmet,
      isLocked,
      lockedAriaLabel: t("conditionLockedAria", { field: label }),
      onLockedClick: () => setPending({ field, label }),
    };
  };

  /**
   * Dependent-dropdown lever for SELECT fields: drop options whose per-option
   * condition the guest's current answers don't meet (soft — an already-held
   * value is kept so editing never silently discards it). Mirrors
   * AdditionFields.optionFilteredField but resolves a built-in field's current
   * value from the top-level form state (built-ins aren't under
   * `additionalFields`). No-op for non-select fields / fields without
   * optionConditions.
   */
  const optionFiltered = (field: Field): Field => {
    const isSelect =
      field.dataType === DataType.SINGLE_SELECT ||
      field.dataType === DataType.MULTI_SELECT;
    if (!isSelect || !field.optionConditions || !field.options) return field;
    const rawVal = field.additionalField
      ? creationFields.additionalFields?.[field.name]
      : (creationFields as unknown as Record<string, unknown>)[field.name];
    const selected = Array.isArray(rawVal)
      ? rawVal.map(String)
      : rawVal != null && rawVal !== ""
        ? [String(rawVal)]
        : [];
    const filtered = field.options.filter((opt) => {
      const cond = field.optionConditions?.[opt];
      return (
        !cond || evaluateConditionGroup(cond, evalData) || selected.includes(opt)
      );
    });
    if (filtered.length === field.options.length) return field;
    return { ...field, options: filtered };
  };

  const confirmDialog = pending ? (
    <PDeleteConfirmation
      title={t("conditionLockedTitle")}
      message={t("conditionLockedMessage", { field: pending.label })}
      primaryButtonText={t("conditionLockedConfirm")}
      isLoading={false}
      onCloseAction={() => setPending(null)}
      onConfirmAction={async () => {
        const k = keyOf(pending.field);
        setUnlocked((prev) => new Set(prev).add(k));
        setPending(null);
      }}
    />
  ) : null;

  return { evalData, getLock, optionFiltered, confirmDialog };
}

/**
 * Generic locked wrapper for field inputs that aren't a plain `DataTypeInput`
 * (which has its own `locked` prop) — e.g. the status cell, credential cells,
 * the address autocomplete. Keeps the child visually identical but genuinely
 * inert (no keyboard/pointer/paste) and overlays a click-catcher that opens the
 * unlock confirm. `inert` (React 19) removes the subtree from the tab order and
 * blocks focus/keydown/activation — `pointer-events-none` alone would not.
 */
export const LockedFieldWrapper: React.FC<{
  locked: boolean;
  ariaLabel?: string;
  onClick?: () => void;
  children: React.ReactNode;
}> = ({ locked, ariaLabel, onClick, children }) => {
  if (!locked) return <>{children}</>;
  return (
    <div className="relative">
      <div
        inert
        aria-hidden
        className="pointer-events-none select-none opacity-60"
      >
        {children}
      </div>
      <button
        type="button"
        aria-label={ariaLabel}
        onClick={onClick}
        className="absolute inset-0 h-full w-full cursor-pointer rounded-[10px]"
      />
    </div>
  );
};

/** Small lock chip for labels of locked non-DataTypeInput fields. */
export const FieldLockIcon: React.FC = () => (
  <IconLock size={14} className="font-tertiary shrink-0" aria-hidden />
);
